import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import styles from './InputField.module.css';

interface InputFieldProps {
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  // Native input
  type?: 'text' | 'number';
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  list?: string;
  // Custom content rendered inside the input-styled container (colour/icon pickers etc.)
  children?: ReactNode;
  onClick?: () => void;
  active?: boolean;
}

export const InputField = ({
  label,
  required,
  error,
  disabled,
  className,
  type = 'text',
  value,
  onChange,
  placeholder,
  min,
  max,
  children,
  onClick,
  active,
  onKeyDown,
  list,
}: InputFieldProps) => {
  const containerCls = [
    styles.input,
    error && styles.inputError,
    disabled && styles.inputDisabled,
    children && styles.inputCustom,
    active && styles.inputActive,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperCls = [styles.wrapper, className].filter(Boolean).join(' ');

  return (
    <div className={wrapperCls}>
      {label && (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.asterisk}>*</span>}
        </span>
      )}

      {children ? (
        <div className={containerCls} onClick={onClick}>{children}</div>
      ) : (
        <input
          className={containerCls}
          type={type}
          value={value ?? ''}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          min={min}
          max={max}
          disabled={disabled}
          list={list}
        />
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};
